import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import subscriptionService, { SubscriptionDetails } from '../../services/subscriptionService';

const SubscriptionStatusBanner: React.FC = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionStatus();
    // Check dismissed state from localStorage
    const dismissedUntil = localStorage.getItem('subscriptionBannerDismissed');
    if (dismissedUntil) {
      const dismissedDate = new Date(dismissedUntil);
      if (dismissedDate > new Date()) {
        setDismissed(true);
      } else {
        localStorage.removeItem('subscriptionBannerDismissed');
      }
    }
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const [sub, days] = await Promise.all([
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getDaysUntilExpiry(),
      ]);
      setSubscription(sub);
      setDaysRemaining(days);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Dismiss for 24 hours
    const dismissUntil = new Date();
    dismissUntil.setHours(dismissUntil.getHours() + 24);
    localStorage.setItem('subscriptionBannerDismissed', dismissUntil.toISOString());
  };

  const handleUpgrade = () => {
    navigate('/subscription');
  };

  if (loading || dismissed || !subscription) {
    return null;
  }

  // Determine if we should show a banner
  const isTrial = subscription.currentPlan === 'trial';
  const isExpired = subscription.status === 'expired';
  const isGracePeriod = subscription.status === 'grace_period';
  const isCancelled = subscription.status === 'cancelled';
  const isTrialEndingSoon = isTrial && daysRemaining > 0 && daysRemaining <= 7;

  // Don't show banner if subscription is active and not trial ending soon
  if (
    !isExpired &&
    !isGracePeriod &&
    !isCancelled &&
    !isTrialEndingSoon
  ) {
    return null;
  }

  // Determine banner style and content
  let bannerStyle = '';
  let icon = null;
  let title = '';
  let message = '';
  let ctaText = '';
  let allowDismiss = true;

  if (isExpired || isCancelled) {
    bannerStyle = 'bg-red-600';
    icon = <ExclamationTriangleIcon className="h-6 w-6 text-white" />;
    title = 'Subscription Expired';
    message = 'Your subscription has expired. Upgrade now to restore access to all features.';
    ctaText = 'Upgrade Now';
    allowDismiss = false; // Don't allow dismissing expired subscription warning
  } else if (isGracePeriod) {
    bannerStyle = 'bg-yellow-600';
    icon = <ExclamationTriangleIcon className="h-6 w-6 text-white" />;
    title = 'Grace Period';
    message = `Your subscription expired but you have ${daysRemaining} days left in your grace period. Renew now to avoid losing access.`;
    ctaText = 'Renew Subscription';
    allowDismiss = false;
  } else if (isTrialEndingSoon) {
    bannerStyle = 'bg-gradient-to-r from-indigo-600 to-purple-600';
    icon = <SparklesIcon className="h-6 w-6 text-white" />;
    title = 'Free Trial Ending Soon';
    message = `Your free trial ends in ${daysRemaining} days. Upgrade now to keep all your data and continue using all features.`;
    ctaText = 'View Plans';
    allowDismiss = true;
  }

  return (
    <div className={`${bannerStyle} shadow-lg`}>
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center flex-1 min-w-0">
            <div className="flex-shrink-0">{icon}</div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-white">
                <span className="font-bold">{title}:</span> {message}
              </p>
            </div>
          </div>
          <div className="flex items-center mt-3 sm:mt-0 sm:ml-4 space-x-3">
            <button
              onClick={handleUpgrade}
              className="bg-white px-4 py-2 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-100 shadow-sm transition-colors"
            >
              {ctaText}
            </button>
            {allowDismiss && (
              <button
                onClick={handleDismiss}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="Dismiss"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatusBanner;
